import { api, getAccessToken } from './client';
import type { SessionMetricsResponse } from '@/types';

export function getSessionMetrics(id: string): Promise<SessionMetricsResponse> {
  return api<SessionMetricsResponse>(`/api/sessions/${id}/metrics`);
}

/**
 * Fetch an authenticated binary GET as a blob and trigger a client-side
 * download. A plain <a href> won't carry the bearer token, so we fetch with the
 * Authorization header and save the result under `filename`.
 */
async function downloadBlob(path: string, filename: string, failLabel: string): Promise<void> {
  const token = getAccessToken();
  const res = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = `${failLabel} (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string; detail?: { message?: string } };
      msg = b.message ?? b.detail?.message ?? msg;
    } catch {
      /* non-JSON */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Download the session PDF report. */
export function downloadReport(id: string): Promise<void> {
  return downloadBlob(`/api/sessions/${id}/report`, `session-${id.slice(0, 8)}.pdf`, 'Report failed');
}

/** Download the raw per-tick metrics as CSV for offline analysis. */
export function downloadMetricsCsv(id: string): Promise<void> {
  return downloadBlob(
    `/api/sessions/${id}/metrics.csv`,
    `session-${id.slice(0, 8)}-metrics.csv`,
    'CSV export failed',
  );
}
